import { useEffect, useState } from "react";
import { createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { isEmpty } from "lodash";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  deliverySuccessMessage,
  toastErrorMessage,
} from "../../../utils/toastMessage/toastMessage";
import {
  getPassengersUserService,
  getAirportListCharter724UserService,
  getAvailableCharter724UserService,
  getAvailable15DaysCharter724UserService,
  getCaptchaCharter724UserService,
  reservationCharter724UserService,
} from "../../../services/userServices";
import { convertRoleToPath, getISOStringDate } from "../../../utils";

export const flightUserContext = createContext({
  airports: [],
  setAirports: () => {},
  passengers: [],
  setPassengers: () => {},
  flights: [],
  setFlights: () => {},
  avail15Days: [],
  setAvail15Days: () => {},
  flight: {},
  setFlight: () => {},
  originItem: {},
  setOriginItem: () => {},
  destinationItem: {},
  setDestinationItem: () => {},
  date: "",
  setDate: () => {},
  originFilter: "",
  setOriginFilter: () => {},
  destinationFilter: "",
  setDestinationFilter: () => {},
  showOriginItems: null,
  setShowOriginItems: () => {},
  showDestinationItems: null,
  setShowDestinationItems: () => {},
  handleGetAirportList: () => {},
  handleGetAvailable15Days: () => {},
  handleGetAvailable: () => {},
  handleGetCaptcha: () => {},
  handleReservation: () => {},
  handleGetPassengers: () => {},
});

const FlightUserContext = ({ children }) => {
  const auth = useSelector((state) => state.auth);

  const dispatch = useDispatch();

  const { pathname } = useLocation();

  const [airports, setAirports] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [flights, setFlights] = useState([]);
  const [avail15Days, setAvail15Days] = useState([]);
  const [flight, setFlight] = useState({});
  const [originItem, setOriginItem] = useState({});
  const [destinationItem, setDestinationItem] = useState({});
  const [date, setDate] = useState("");
  const [originFilter, setOriginFilter] = useState("");
  const [destinationFilter, setDestinationFilter] = useState("");
  const [showOriginItems, setShowOriginItems] = useState(false);
  const [showDestinationItems, setShowDestinationItems] = useState(false);

  useEffect(() => {
    setAirports([]);
    setFlights([]);
    setAvail15Days([]);
    setFlight({});
    setOriginItem({});
    setDestinationItem({});
    setDate("");
    setOriginFilter("");
    setDestinationFilter("");
  }, [pathname]);

  // Get Airport List
  const handleGetAirportList = async (event) => {
    event?.preventDefault();

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getAirportListCharter724UserService();
      if (status === 200) {
        setAirports(data.result?.data);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Available 15 Days
  const handleGetAvailable15Days = async (event) => {
    event?.preventDefault();

    // Validation
    if (isEmpty(originItem)) return;
    if (isEmpty(destinationItem)) return;

    const objData = {
      from_flight: originItem?.iatA_airport,
      to_flight: destinationItem?.iatA_airport,
    };

    try {
      const { status, data } = await getAvailable15DaysCharter724UserService(
        objData
      );
      if (status === 200) {
        setAvail15Days(data.result?.data);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    }
  };

  // Get Available
  const handleGetAvailable = async (event) => {
    event?.preventDefault();

    // Validation
    if (isEmpty(originItem))
      return toastErrorMessage("انتخاب شهر مبدا الزامی است");
    if (isEmpty(destinationItem))
      return toastErrorMessage("انتخاب شهر مقصد الزامی است");
    if (!date) return toastErrorMessage("تاریخ سفر الزامی است");

    const objData = {
      from_flight: originItem?.iatA_airport,
      to_flight: destinationItem?.iatA_airport,
      date_flight: getISOStringDate(date),
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getAvailableCharter724UserService(objData);
      if (status === 200) {
        setFlights(data.result?.data);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Captcha
  const handleGetCaptcha = async (fly) => {
    const objData = {
      from_flight: fly.from,
      to_flight: fly.to,
      date_flight: fly.date_flight,
      time_flight: fly.time_flight,
      number_flight: fly.number_flight,
      ajency_online_ID: fly.ajency_online_ID,
      cabinclass: fly.cabinclass,
      sellingType: fly.sellingType,
      airline: fly.airline,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCaptchaCharter724UserService(objData);
      if (status === 200) {
        setFlight({ ...fly, ...data.result?.data });
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Reservation
  const handleReservation = async (userData) => {
    const objData = {
      ...userData,
      id_request: flight.id_request,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await reservationCharter724UserService(objData);
      if (status === 200) {
        deliverySuccessMessage(
          data.resultMessage,
          `/${convertRoleToPath(auth.role)}/fly-orders`,
          "5000"
        );
        setFlight({});
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Passengers
  const handleGetPassengers = async (filter) => {
    const objData = {
      filter,
    };
    try {
      const { status, data } = await getPassengersUserService(objData);
      if (status === 200) {
        setPassengers(data.result?.passengers);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    }
  };

  return (
    <flightUserContext.Provider
      value={{
        airports,
        setAirports,
        passengers,
        setPassengers,
        flights,
        setFlights,
        avail15Days,
        setAvail15Days,
        flight,
        setFlight,
        originItem,
        setOriginItem,
        destinationItem,
        setDestinationItem,
        date,
        setDate,
        originFilter,
        setOriginFilter,
        destinationFilter,
        setDestinationFilter,
        showOriginItems,
        setShowOriginItems,
        showDestinationItems,
        setShowDestinationItems,
        handleGetAirportList,
        handleGetAvailable15Days,
        handleGetAvailable,
        handleGetCaptcha,
        handleReservation,
        handleGetPassengers,
      }}
    >
      {children}
    </flightUserContext.Provider>
  );
};

export default FlightUserContext;
